import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useT } from '../../i18n'
import { cx } from '../cx'
import { Icon } from '../icons'
import { Button } from './Button'
import { IconButton } from './IconButton'
import styles from './Toast.module.css'

/** How long an action stays undoable (docs/research/05 §4.4: undo over confirm). */
export const UNDO_WINDOW_MS = 10_000
const MAX_VISIBLE = 3

export interface ToastOptions {
  message: ReactNode
  /** Shows an Undo button. Called at most once, only if the person presses it in time. */
  undo?: () => void
  /** Called when the toast leaves without Undo (timeout or Dismiss): commit deferred work here. */
  onExpire?: () => void
  durationMs?: number
  /** 'error' shows a warning icon instead of the default check (the words still carry the meaning). */
  tone?: 'success' | 'info' | 'error'
}

interface ToastItem extends ToastOptions {
  id: number
}

interface ToastApi {
  show: (options: ToastOptions) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

let nextId = 1

const TONE_ICONS = { success: 'check', info: 'info', error: 'warning' } as const

/**
 * Hosts the toast region. The live region is always mounted (polite), so screen readers
 * announce each new message; toasts pause their countdown while hovered or focused
 * (WCAG 2.2.1), and never steal focus.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  // Mirror of `items` so show() can decide what overflows without side effects in an updater.
  const itemsRef = useRef<ToastItem[]>([])

  const remove = useCallback((id: number) => {
    itemsRef.current = itemsRef.current.filter((item) => item.id !== id)
    setItems(itemsRef.current)
  }, [])

  const show = useCallback((options: ToastOptions) => {
    const id = nextId++
    const next = [...itemsRef.current, { ...options, id }]
    const overflow = next.slice(0, Math.max(0, next.length - MAX_VISIBLE))
    itemsRef.current = next.slice(-MAX_VISIBLE)
    setItems(itemsRef.current)
    // Oldest toasts beyond the limit leave right away, so their actions commit now.
    for (const dropped of overflow) dropped.onExpire?.()
    return id
  }, [])

  const api = useMemo<ToastApi>(() => ({ show, dismiss: remove }), [show, remove])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastRegion items={items} onDone={remove} />
    </ToastContext.Provider>
  )
}

function ToastRegion({ items, onDone }: { items: ToastItem[]; onDone: (id: number) => void }) {
  const t = useT()
  return (
    // The live region sits on the wrapper: a role on the <ol> would strip its list semantics
    // and orphan every toast's <li>.
    <section
      className={styles.region}
      role="status"
      aria-live="polite"
      aria-label={t.common.toast.region}
    >
      <ol className={styles.list}>
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDone={onDone} />
        ))}
      </ol>
    </section>
  )
}

function ToastCard({ item, onDone }: { item: ToastItem; onDone: (id: number) => void }) {
  const t = useT()
  const [paused, setPaused] = useState(false)
  const settled = useRef(false)
  const remaining = useRef(item.durationMs ?? UNDO_WINDOW_MS)

  const finish = useCallback(
    (undone: boolean) => {
      if (settled.current) return
      settled.current = true
      if (undone) item.undo?.()
      else item.onExpire?.()
      onDone(item.id)
    },
    [item, onDone],
  )

  useEffect(() => {
    if (paused) return
    const started = Date.now()
    const timer = setTimeout(() => finish(false), remaining.current)
    return () => {
      clearTimeout(timer)
      remaining.current -= Date.now() - started
    }
  }, [paused, finish])

  return (
    <li
      className={cx(styles.toast, item.undo && styles.withUndo)}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon name={TONE_ICONS[item.tone ?? 'success']} size={20} className={styles.icon} />
      <p className={styles.message}>{item.message}</p>
      {/* Beside the message; under it on a narrow screen when there is an Undo. */}
      <div className={styles.actions}>
        {item.undo && (
          <Button variant="secondary" icon="undo" onClick={() => finish(true)}>
            {t.common.actions.undo}
          </Button>
        )}
        <IconButton icon="x" label={t.common.actions.dismiss} onClick={() => finish(false)} />
      </div>
    </li>
  )
}
